// src/components/ai/copilot/InstructorQuickAiDraftInput.js
import React, { useState } from 'react';
import AiButton from '../AiButton';
import AiDraftContextChips from './AiDraftContextChips';
import {
  buildInstructorContextClause,
  emptyQuickInput,
  isQuickInputEmpty,
  MAX_NOTES_LENGTH,
  TONE_OPTIONS,
} from '../../../utils/instructorCopilot';

/**
 * The reusable quick-input entry point for the copilot workflow.
 * Composes notes textarea + optional field selectors.
 * Manages all quick-input state internally.
 * Calls onGenerate(quickInput, instructorContextClause) when the instructor
 * clicks generate — does NOT make AI calls itself.
 *
 * Props:
 *   onGenerate       — (quickInput, contextClause: string) => void
 *   loading          — bool
 *   disabled         — bool
 *   placeholder      — string
 *   generateLabel    — string (default "Generate Draft")
 *   showSkillPicker  — bool — shows a text input for target skill
 *   showDurationInput — bool
 *   showTonePicker   — bool
 *   showTagChips     — bool
 *   availableTags    — [{ key, label }]
 *   initialQuickInput — partial QuickInput — pre-fills fields (e.g. from page's selectedSkillId)
 *   signalOnlyWarning — bool — shows "signals only" note when input is empty (default false)
 */
function InstructorQuickAiDraftInput({
  onGenerate,
  loading = false,
  disabled = false,
  placeholder = 'Quick coaching notes — rough thoughts, key observations, focus areas…',
  generateLabel = 'Generate Draft',
  showSkillPicker = false,
  showDurationInput = false,
  showTonePicker = false,
  showTagChips = false,
  availableTags = [],
  initialQuickInput = {},
  signalOnlyWarning = false,
}) {
  const [quickInput, setQuickInput] = useState(() => ({
    ...emptyQuickInput(),
    ...initialQuickInput,
  }));

  const update = (field, value) => setQuickInput((current) => ({ ...current, [field]: value }));

  const handleGenerate = () => {
    if (loading || disabled) return;
    const contextClause = buildInstructorContextClause(quickInput);
    onGenerate?.(quickInput, contextClause);
  };

  const isDisabled = disabled || loading;
  const charCount = (quickInput.notes || '').length;
  const nearLimit = charCount > MAX_NOTES_LENGTH * 0.8;
  const isEmpty = isQuickInputEmpty(quickInput);

  return (
    <div className="space-y-3">
      {/* Notes textarea — always shown */}
      <div>
        <label className="text-xs font-semibold text-slate-500">
          Coach notes <span className="font-normal text-slate-400">(optional)</span>
        </label>
        <textarea
          className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:bg-slate-50"
          rows={3}
          value={quickInput.notes}
          onChange={(e) => update('notes', e.target.value)}
          placeholder={placeholder}
          disabled={isDisabled}
          maxLength={MAX_NOTES_LENGTH}
        />
        <p className={`mt-1 text-right text-xs ${nearLimit ? 'text-amber-500' : 'text-slate-400'}`}>
          {charCount}/{MAX_NOTES_LENGTH}
        </p>
      </div>

      {/* Optional: target skill text input */}
      {showSkillPicker ? (
        <div>
          <label className="text-xs font-semibold text-slate-500">Target skill</label>
          <input
            type="text"
            className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-300"
            value={quickInput.targetSkillId}
            onChange={(e) => update('targetSkillId', e.target.value)}
            placeholder="e.g. carving, parallel turns"
            disabled={isDisabled}
          />
        </div>
      ) : null}

      {/* Optional: duration */}
      {showDurationInput ? (
        <div>
          <label className="text-xs font-semibold text-slate-500">Duration (minutes)</label>
          <input
            type="number"
            min={15}
            max={240}
            step={15}
            className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-300"
            value={quickInput.duration ?? ''}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              update('duration', Number.isNaN(val) ? null : val);
            }}
            placeholder="60"
            disabled={isDisabled}
          />
        </div>
      ) : null}

      {/* Optional: tone */}
      {showTonePicker ? (
        <div>
          <label className="text-xs font-semibold text-slate-500">Tone</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {TONE_OPTIONS.map((tone) => (
              <button
                key={tone}
                type="button"
                onClick={() => update('tone', tone)}
                disabled={isDisabled}
                className={`rounded-full px-3 py-1 text-xs font-medium capitalize transition-colors ${
                  quickInput.tone === tone
                    ? 'bg-violet-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-violet-100 hover:text-violet-700'
                }`}
              >
                {tone}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {/* Optional: focus area chips */}
      {showTagChips ? (
        <AiDraftContextChips
          availableTags={availableTags}
          selectedTags={quickInput.focusTags}
          onChange={(tags) => update('focusTags', tags)}
        />
      ) : null}

      {/* Signal-only warning when no input and flag is set */}
      {signalOnlyWarning && isEmpty ? (
        <p className="text-xs text-slate-400">
          No context added — AI will generate from client signals only.
        </p>
      ) : null}

      <AiButton
        onClick={handleGenerate}
        loading={loading}
        disabled={isDisabled}
        label={generateLabel}
      />
    </div>
  );
}

export default InstructorQuickAiDraftInput;
